import { useState } from 'react';
import { HoldReasonSelect } from './HoldReasonSelect';
import { usePauseWorkOrder } from '../hooks/useHoldDecisionActions';

export interface PauseWorkOrderDialogProps {
  workOrderId: string;
  workOrderNumber?: string;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * AM-050 WPD-01/02 & Gate G5:
 * دیالوگ استاندارد توقف سفارش کار (Work Order) در شاپ‌فلور و سفارش‌های تولید.
 * ارسال دلیل توقف (HoldReasonCode) اجباری است و تا زمان انتخاب دلیل، دکمه تأیید غیرفعال می‌ماند.
 */
export function PauseWorkOrderDialog({
  workOrderId,
  workOrderNumber,
  open,
  onClose,
  onSuccess,
}: PauseWorkOrderDialogProps) {
  const [reasonCodeId, setReasonCodeId] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const { mutate, isPending, error } = usePauseWorkOrder(workOrderId);

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reasonCodeId) return;

    mutate(
      {
        hold_reason_id: reasonCodeId,
        hold_notes: notes.trim(),
      },
      {
        onSuccess: () => {
          onSuccess?.();
          onClose();
        },
      }
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      dir="rtl"
    >
      <form onSubmit={handleSubmit} className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-1 text-lg font-semibold text-gray-900">
          توقف سفارش کار {workOrderNumber ? `(${workOrderNumber})` : ''}
        </h2>
        <p className="mb-4 text-sm text-gray-500">
          جهت توقف موقت این مرحله از عملیات، انتخاب دلیل توقف الزامی است.
        </p>

        <div className="mb-4">
          <HoldReasonSelect value={reasonCodeId} onChange={setReasonCodeId} required />
        </div>

        <div className="mb-4 flex flex-col gap-1">
          <label htmlFor="pause-notes" className="text-sm font-medium text-gray-700">
            توضیحات تکمیلی (اختیاری)
          </label>
          <textarea
            id="pause-notes"
            className="min-h-[80px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="توضیحات تکمیلی در مورد توقف این کارگاه یا عملیات..."
          />
        </div>

        {error && (
          <p className="mb-3 text-sm text-red-600">
            خطا در ثبت توقف سفارش کار. لطفاً دوباره تلاش کنید.
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
            disabled={isPending}
          >
            انصراف
          </button>
          <button
            type="submit"
            className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
            disabled={!reasonCodeId || isPending}
          >
            {isPending ? 'در حال ثبت…' : 'تأیید توقف کار'}
          </button>
        </div>
      </form>
    </div>
  );
}
